<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipQualityText, equipTypeList, noPercentEquipAttrKeyList, supremeAffixLevelQualityMap } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getEngravePrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { SupremeEngraveRes } from '@/types/smithy'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'
import { cloneDeep } from 'lodash'
import type { UserAttrType } from '@/types/user'
import { supremeAffixCollection, engraveSupremeMaxQulityMap } from '@/core/equip/supreme'
import { getSkillNameById } from '@/core/skill'
import { BasicAttrTextEnum } from '@/enums'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (!item.supremeAttrs) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

const isUseEquipEngraveMode = ref(false)
const sameNameLevelItemList = computed<EquipItemType[]>(() => {
  if (!equipInfo.value) return []
  const equippedIdList = Object.values(userStore.equippedList)
  return userStore.itemList
    .filter((item) => {
      if (item.id === equipInfo.value?.id) return false
      if (equippedIdList.includes(item.id)) return false
      if (item.isLock) return false
      if (!item.supremeAttrs || !equipInfo.value?.supremeAttrs) return false
      return item.equipId === equipInfo.value.equipId && Number(item.supremeAttrs.level) === Number(equipInfo.value.supremeAttrs.level)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

const engraveMaxQuality = computed(() => {
  if (!equipInfo.value || !equipInfo.value.supremeAttrs) return ''
  return engraveSupremeMaxQulityMap[equipInfo.value.supremeAttrs.level]
})

function handleChoice(item: EquipItemType | undefined) {
  if (item && !item.supremeAttrs) return ElMessage.warning('仅至臻装备可刻印')
  equipItemId.value = item?.id || ''
  useCoupon.value = false
  isUseEquipEngraveMode.value = false
}

function handleEnterUseEquipEngrave() {
  if (!equipInfo.value) return ElMessage.warning('请先放入需要刻印的装备')
  if (!sameNameLevelItemList.value.length) return ElMessage.warning('暂无可用于刻印的同名同品质装备')
  useCoupon.value = false
  isUseEquipEngraveMode.value = true
}

function handleChoiceUseEquipEngraveItem(item: EquipItemType) {
  if (!equipInfo.value) return
  ElMessageBox.confirm(
    `<p class="affix">确定消耗【<span class="info">${item.name}</span>】对当前装备进行<span class="grow">免费刻印</span>吗？（该操作将<span class="shrink">无法撤销</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await handleEngrave(item.id)
      isUseEquipEngraveMode.value = false
    })
    .catch(() => {})
}

const useCoupon = ref(false)
function beforeUseCouponChange() {
  if (!userStore.consumables.supremeEngraveCoupon) {
    ElMessage.warning('您未拥有至臻刻印券')
    return false
  }
  if (isUseEquipEngraveMode.value) {
    ElMessage.warning('使用装备刻印时无法使用刻印券')
    return false
  }
  return true
}

const resShow = ref(false)
const resEquipInfo = ref()
const isRecord = ref(false)
async function handleEngrave(useEquipItemId?: string) {
  if (!equipInfo.value) return
  const { id, supremeAttrs, supremeEngraveAttr } = equipInfo.value
  const price = getEngravePrice(supremeEngraveAttr?.count, supremeAttrs?.level)

  const {
    engraveAttr: resEngraveAttr,
    userAttr,
    isRecord: resIsRecord,
  } = await socketEmitAsync<SupremeEngraveRes>({
    type: 'supremeEngraveEquip',
    data: { id, useCoupon: useCoupon.value, useEquipItemId },
  })
  resEquipInfo.value = undefined
  isRecord.value = resIsRecord
  if (userAttr) userStore.userAttr = userAttr
  if (!resIsRecord) {
    if (useEquipItemId) {
      const useEquipItemIndex = userStore.itemList.findIndex((item) => item.id === useEquipItemId)
      if (useEquipItemIndex >= 0) userStore.itemList.splice(useEquipItemIndex, 1)
    } else if (useCoupon.value) {
      userStore.consumables.supremeEngraveCoupon--
      if (userStore.consumables.supremeEngraveCoupon <= 0) useCoupon.value = false
    } else {
      userStore.gold -= price
    }
  }

  const item = userStore.itemList.find((item) => item.id === id)
  if (!item || !equipInfo.value) return
  if (resEngraveAttr?.count === 1) {
    item.supremeEngraveAttr = resEngraveAttr
    equipInfo.value.supremeEngraveAttr = resEngraveAttr
  } else {
    resEquipInfo.value = {
      ...cloneDeep(equipInfo.value),
      supremeEngraveAttr: resEngraveAttr,
    }
  }

  resShow.value = true
}
async function handleChoiceEngraveRes(type: 0 | 1) {
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">${type === 0 ? '保留' : '覆盖'}</span>刻印吗？（该操作将导致选中的装备<span class="shrink">销毁</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      if (!equipInfo.value || !resEquipInfo.value) return

      const itemId = equipInfo.value.id

      const userAttr = await socketEmitAsync<UserAttrType>({ type: 'choiceSupremeEngraveRes', data: { itemId, choiceIndex: type } })
      const item = userStore.itemList.find((item) => item.id === itemId)
      if (!item) return
      if (userAttr) {
        userStore.userAttr = userAttr

        const resEngraveAttr = resEquipInfo.value.supremeEngraveAttr
        item.supremeEngraveAttr = resEngraveAttr
        equipInfo.value.supremeEngraveAttr = resEngraveAttr
      } else {
        if (!item.supremeEngraveAttr || !equipInfo.value.supremeEngraveAttr) return
        item.supremeEngraveAttr.count++
      }

      resShow.value = false
      ElMessage.success('刻印确认成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="enchant">
    <div class="enchant-table border-wrap">
      <h4>锻造台</h4>
      <div class="enchant-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>刻印说明</h4>
            <p>1. 通过<span class="info">刻印</span>，可为装备<span class="info">新增</span>一条额外词缀。</p>
            <p>2. 刻印产出的词缀品质及概率受装备品级影响。</p>
            <p>
              3. 首次刻印<span class="grow">免费</span>，而后刻印花费将随<span class="info">刻印次数</span>逐步增加，直至
              <span class="grow">10次</span>时达到最大值，同时刻印的花费受装备的<span class="info">品级</span>影响。
            </p>
            <p>4. 第二次刻印开始，可自主选择保留/覆盖刻印词缀，若您忘记选择，可再次对该装备刻印以继续选择。</p>
            <p>5. 可使用<span class="info">同名且同阶级</span>的装备作为材料进行刻印，不消耗金币。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <p style="display: flex; align-items: center">
            <span>刻印次数：{{ equipInfo.supremeEngraveAttr?.count || 0 }}</span>
            <span
              >使用刻印券（{{ userStore.consumables.supremeEngraveCoupon }}）：<el-switch
                v-model="useCoupon"
                size="small"
                :before-change="beforeUseCouponChange"
            /></span>
          </p>
          <p>
            刻印花费：{{
              useCoupon || isUseEquipEngraveMode
                ? 0
                : formatNumberWithCommas(getEngravePrice(equipInfo.supremeEngraveAttr?.count, equipInfo.supremeAttrs?.level))
            }}
            金币
          </p>
          <p style="justify-content: flex-start">
            刻印词缀：
            <span
              v-if="equipInfo.supremeEngraveAttr?.affixKey"
              :class="supremeAffixLevelQualityMap[supremeAffixCollection[equipInfo.supremeEngraveAttr.affixKey].level]"
            >
              {{ equipInfo.supremeEngraveAttr ? supremeAffixCollection[equipInfo.supremeEngraveAttr.affixKey].name : '无' }}
            </span>
          </p>
          <p v-if="engraveMaxQuality" style="justify-content: flex-start">
            刻印词缀最高品质：<span :class="engraveMaxQuality">{{ equipQualityText[engraveMaxQuality] }}</span>
          </p>
        </div>
        <div class="btn-wrap engrave-btn-wrap">
          <el-button type="warning" plain @click="handleEngrave()"> 开始刻印 </el-button>
          <el-button v-if="!isUseEquipEngraveMode" type="primary" plain @click="handleEnterUseEquipEngrave" style="margin-left: 0">
            使用装备刻印
          </el-button>
          <el-button v-if="isUseEquipEngraveMode" type="danger" plain @click="isUseEquipEngraveMode = false" style="margin-left: 0">
            取消装备刻印
          </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px" v-show="!isUseEquipEngraveMode">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package" v-show="!isUseEquipEngraveMode">
      <h4>背包</h4>
      <p class="cur-gold">
        <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package" v-show="isUseEquipEngraveMode">
      <h4>选择同名同品质装备作为材料</h4>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of sameNameLevelItemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoiceUseEquipEngraveItem(item)">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
        <p v-if="!sameNameLevelItemList.length" style="text-align: center">暂无可用于刻印的同名同品质装备</p>
      </div>
    </div>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px" :before-close="() => false">
      <h2 class="affix">
        <span class="grow">{{ isRecord ? '上次刻印结果未确认' : '刻印成功' }}</span>
      </h2>
      <p style="text-align: center" v-show="resEquipInfo">请选择保留的词缀</p>
      <div class="engrave-res">
        <div class="left border-wrap" v-if="equipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="equipInfo.supremeEngraveAttr?.affixKey">
            <p :class="supremeAffixLevelQualityMap[supremeAffixCollection[equipInfo.supremeEngraveAttr.affixKey].level]">
              {{ supremeAffixCollection[equipInfo.supremeEngraveAttr.affixKey].name }}：
            </p>
            <p class="affix" v-for="(val, key) of supremeAffixCollection[equipInfo.supremeEngraveAttr.affixKey].attrs">
              <template v-if="key !== 'skillGrow'">
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span v-if="val && (val as number) > 0">
                  +
                  <span class="grow">{{ val }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
                <span v-else>
                  -
                  <span class="shrink">{{ Math.abs((val as number)!) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </template>
              <template v-else>
                <p v-for="(levelAdd, skillId) in val">
                  <span>{{ getSkillNameById(Number(skillId), true) }}</span>
                  <span
                    >+ <span class="grow">{{ levelAdd }}</span></span
                  >
                </p>
              </template>
            </p>
          </template>
          <div class="btn-wrap" v-show="resEquipInfo">
            <el-button type="primary" plain size="small" @click="handleChoiceEngraveRes(0)">保留</el-button>
          </div>
        </div>
        <div class="right border-wrap" v-if="resEquipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, resEquipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="resEquipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="resEquipInfo.supremeEngraveAttr">
            <p :class="supremeAffixLevelQualityMap[supremeAffixCollection[resEquipInfo.supremeEngraveAttr.affixKey].level]">
              {{ supremeAffixCollection[resEquipInfo.supremeEngraveAttr.affixKey].name }}：
            </p>
            <p class="affix" v-for="(val, key) of supremeAffixCollection[resEquipInfo.supremeEngraveAttr.affixKey].attrs">
              <template v-if="key !== 'skillGrow'">
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span v-if="val && (val as number) > 0">
                  +
                  <span class="grow">{{ val }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
                <span v-else>
                  -
                  <span class="shrink">{{ Math.abs((val as number)!) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </template>
              <template v-else>
                <p v-for="(levelAdd, skillId) in val">
                  <span>{{ getSkillNameById(Number(skillId), true) }}</span>
                  <span
                    >+ <span class="grow">{{ levelAdd }}</span></span
                  >
                </p>
              </template>
            </p>
          </template>
          <div class="btn-wrap">
            <el-button type="warning" plain size="small" @click="handleChoiceEngraveRes(1)">覆盖</el-button>
          </div>
        </div>
      </div>
      <template #footer>
        <p style="text-align: center" v-show="!resEquipInfo">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.enchant {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .enchant-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .enchant-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .engrave-btn-wrap {
    button {
      margin-left: 0;
      margin-bottom: 10px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .engrave-res {
    display: flex;
    justify-content: space-between;
    > div {
      width: calc(50% - 5px);
      margin: 0 auto;
      flex: 1;
    }
    .border-wrap {
      padding: 10px;
      p {
        margin: 4px 0;
      }
    }
  }
}
</style>
